import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutGrid, Package, ShoppingCart, Users, DollarSign,
  Plus, Pencil, Trash2, X, Store, Tag, Upload, Check,
} from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

const EMPTY = {
  title: "", tagline: "", description: "", price: "", category: "Templates",
  image: "", featured: false, file_format: "ZIP",
};
const CATEGORIES = ["AI Packs", "E-books", "Business", "Bundles", "Templates"];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [discounts, setDiscounts] = useState([]);
  const [discountForm, setDiscountForm] = useState({ code: "", type: "percent", value: "", usage_limit: "", expires_at: "" });
  const [savingDiscount, setSavingDiscount] = useState(false);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/analytics").then((r) => setAnalytics(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/admin/discounts").then((r) => setDiscounts(r.data));
  };

  useEffect(() => { loadAll(); }, []);

  const saveDiscount = async (e) => {
    e.preventDefault();
    setSavingDiscount(true);
    const payload = {
      code: discountForm.code,
      type: discountForm.type,
      value: parseFloat(discountForm.value) || 0,
      usage_limit: discountForm.usage_limit ? parseInt(discountForm.usage_limit) : null,
      expires_at: discountForm.expires_at ? new Date(discountForm.expires_at).toISOString() : null,
      active: true,
    };
    try {
      await api.post("/admin/discounts", payload);
      toast.success("Discount code created");
      setDiscountForm({ code: "", type: "percent", value: "", usage_limit: "", expires_at: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingDiscount(false);
    }
  };

  const removeDiscount = async (id) => {
    if (!window.confirm("Delete this code?")) return;
    try {
      await api.delete(`/admin/discounts/${id}`);
      toast.success("Code deleted");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, price: String(p.price) });
    setModalOpen(true);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/products/${editing.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      const updated = { ...editing, has_file: true, download_filename: file.name };
      setEditing(updated);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setModalOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const NAV = [
    { key: "overview", label: "Overview", icon: LayoutGrid },
    { key: "products", label: "Products", icon: Package },
    { key: "orders", label: "Orders", icon: ShoppingCart },
    { key: "discounts", label: "Discounts", icon: Tag },
  ];

  return (
    <div data-testid="admin-dashboard" className="flex min-h-screen bg-bone font-sans">
      {/* Sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-obsidian/15 bg-white md:flex md:flex-col">
        <div className="border-b border-obsidian/15 p-6">
          <p className="font-serif text-xl tracking-tighter text-obsidian">LUX<span className="text-signal">•</span>DROP</p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-obsidian/40">Admin Console</p>
        </div>
        <nav className="flex-1 p-3">
          {NAV.map((n) => (
            <button
              key={n.key}
              data-testid={`admin-tab-${n.key}`}
              onClick={() => setTab(n.key)}
              className={`mb-1 flex w-full items-center gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${
                tab === n.key ? "bg-obsidian text-white" : "text-obsidian/60 hover:bg-secondary"
              }`}
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-obsidian/15 p-3">
          <Link to="/" className="flex items-center gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-obsidian/60 hover:bg-secondary">
            <Store className="h-4 w-4" /> View store
          </Link>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        {/* Mobile tab bar */}
        <div className="flex gap-1 border-b border-obsidian/15 bg-white p-2 md:hidden">
          {NAV.map((n) => (
            <button key={n.key} onClick={() => setTab(n.key)}
              className={`flex-1 px-2 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] ${tab === n.key ? "bg-obsidian text-white" : "text-obsidian/60"}`}>
              {n.label}
            </button>
          ))}
        </div>

        <div className="p-6 md:p-10">
          <h1 className="font-serif text-3xl font-light tracking-tighter text-obsidian md:text-4xl">
            {NAV.find((n) => n.key === tab)?.label}
          </h1>

          {/* Overview */}
          {tab === "overview" && (
            <div className="mt-8 grid grid-cols-2 gap-px border border-obsidian/15 bg-obsidian/15 lg:grid-cols-4">
              {[
                { label: "Revenue", value: stats ? `$${stats.revenue.toFixed(2)}` : "—", icon: DollarSign },
                { label: "Orders", value: stats?.orders ?? "—", icon: ShoppingCart },
                { label: "Products", value: stats?.products ?? "—", icon: Package },
                { label: "Customers", value: stats?.customers ?? "—", icon: Users },
              ].map((s) => (
                <div key={s.label} data-testid={`stat-${s.label.toLowerCase()}`} className="bg-white p-6">
                  <s.icon className="h-5 w-5 text-signal" />
                  <p className="mt-4 font-serif text-3xl tracking-tighter text-obsidian">{s.value}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-obsidian/40">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "overview" && (
            <div className="mt-8 grid gap-8 lg:grid-cols-3">
              <div className="border border-obsidian/15 bg-white p-6 lg:col-span-2" data-testid="revenue-chart">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian/50">Revenue — last 14 days</h2>
                  <span className="font-serif text-lg tracking-tighter text-obsidian">
                    ${analytics ? analytics.revenue_series.reduce((s, d) => s + d.revenue, 0).toFixed(2) : "0.00"}
                  </span>
                </div>
                <div className="mt-6 h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analytics?.revenue_series || []} margin={{ top: 4, right: 12, left: -6, bottom: 0 }}>
                      <defs>
                        <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#E53935" stopOpacity={0.25} />
                          <stop offset="100%" stopColor="#E53935" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#0A0A0A0d" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#525252" }} axisLine={false} tickLine={false} interval={1} />
                      <YAxis tick={{ fontSize: 10, fill: "#525252" }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0, fontSize: 12 }}
                        formatter={(v, n) => [n === "revenue" ? `$${v}` : v, n === "revenue" ? "Revenue" : "Orders"]}
                      />
                      <Area type="monotone" dataKey="revenue" stroke="#E53935" strokeWidth={2} fill="url(#rev)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="border border-obsidian/15 bg-white p-6" data-testid="best-sellers">
                <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian/50">Best sellers</h2>
                <div className="mt-5 space-y-4">
                  {analytics && analytics.best_sellers.length > 0 ? (
                    analytics.best_sellers.map((b, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="font-serif text-lg tracking-tighter text-obsidian/30 w-5">{i + 1}</span>
                        <div className="h-10 w-12 shrink-0 overflow-hidden border border-obsidian/10">
                          {b.image ? <img src={b.image} alt={b.title} className="h-full w-full object-cover" /> : null}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-obsidian">{b.title}</p>
                          <p className="text-[10px] uppercase tracking-[0.15em] text-obsidian/40">{b.qty} sold</p>
                        </div>
                        <span className="font-serif text-lg tracking-tighter text-obsidian">${b.revenue}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-obsidian/50">No sales yet. Your best sellers will appear here.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Products */}
          {tab === "products" && (
            <div className="mt-8">
              <div className="mb-4 flex justify-end">
                <button
                  data-testid="add-product-btn"
                  onClick={openNew}
                  className="inline-flex items-center gap-2 bg-signal px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-red-700"
                >
                  <Plus className="h-4 w-4" /> Add product
                </button>
              </div>
              <div className="overflow-x-auto border border-obsidian/15 bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-obsidian/15 text-[10px] uppercase tracking-[0.2em] text-obsidian/50">
                    <tr>
                      <th className="p-4">Product</th>
                      <th className="p-4">Category</th>
                      <th className="p-4">Price</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-obsidian/10">
                    {products.map((p) => (
                      <tr key={p.id} data-testid={`admin-product-${p.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-12 shrink-0 overflow-hidden border border-obsidian/10">
                              <img src={p.image} alt={p.title} className="h-full w-full object-cover" />
                            </div>
                            <span className="font-medium text-obsidian">{p.title}</span>
                            {p.has_file && (
                              <span className="inline-flex items-center gap-1 border border-obsidian/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-obsidian/60">
                                <Check className="h-3 w-3 text-signal" /> File
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-obsidian/60">{p.category}</td>
                        <td className="p-4 font-serif text-lg tracking-tighter text-obsidian">${p.price}</td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="border border-obsidian/20 p-2 text-obsidian/70 transition-colors hover:border-obsidian hover:text-obsidian">
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="border border-obsidian/20 p-2 text-obsidian/70 transition-colors hover:border-signal hover:text-signal">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Orders */}
          {tab === "orders" && (
            <div className="mt-8 overflow-x-auto border border-obsidian/15 bg-white">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-obsidian/15 text-[10px] uppercase tracking-[0.2em] text-obsidian/50">
                  <tr>
                    <th className="p-4">Order</th>
                    <th className="p-4">Customer</th>
                    <th className="p-4">Items</th>
                    <th className="p-4">Date</th>
                    <th className="p-4 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-obsidian/10">
                  {orders.length === 0 ? (
                    <tr><td colSpan="5" className="p-8 text-center text-obsidian/50">No orders yet.</td></tr>
                  ) : orders.map((o) => (
                    <tr key={o.id} data-testid={`admin-order-${o.id}`}>
                      <td className="p-4 font-medium text-obsidian">{o.order_number}</td>
                      <td className="p-4 text-obsidian/70">
                        {o.name}<br /><span className="text-xs text-obsidian/40">{o.email}</span>
                      </td>
                      <td className="p-4 text-obsidian/60">{o.items.reduce((s, i) => s + i.quantity, 0)}</td>
                      <td className="p-4 text-obsidian/60">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="p-4 text-right font-serif text-lg tracking-tighter text-obsidian">${o.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Discounts */}
          {tab === "discounts" && (
            <div className="mt-8 grid gap-8 lg:grid-cols-3">
              <form onSubmit={saveDiscount} className="border border-obsidian/15 bg-white p-6 lg:col-span-1">
                <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian/50">New code</h2>
                <div className="mt-5 space-y-4">
                  <Field label="Code">
                    <input required data-testid="discount-code" value={discountForm.code}
                      onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value.toUpperCase() })}
                      className={inputCls} placeholder="LAUNCH20" />
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Type">
                      <select value={discountForm.type} onChange={(e) => setDiscountForm({ ...discountForm, type: e.target.value })} className={inputCls}>
                        <option value="percent">Percent %</option>
                        <option value="fixed">Fixed $</option>
                      </select>
                    </Field>
                    <Field label={discountForm.type === "percent" ? "Value (%)" : "Value ($)"}>
                      <input required type="number" step="0.01" data-testid="discount-value" value={discountForm.value}
                        onChange={(e) => setDiscountForm({ ...discountForm, value: e.target.value })} className={inputCls} />
                    </Field>
                  </div>
                  <Field label="Usage limit (optional)">
                    <input type="number" value={discountForm.usage_limit}
                      onChange={(e) => setDiscountForm({ ...discountForm, usage_limit: e.target.value })} className={inputCls} placeholder="Unlimited" />
                  </Field>
                  <Field label="Expires (optional)">
                    <input type="date" value={discountForm.expires_at}
                      onChange={(e) => setDiscountForm({ ...discountForm, expires_at: e.target.value })} className={inputCls} />
                  </Field>
                  <button data-testid="save-discount-btn" type="submit" disabled={savingDiscount}
                    className="w-full bg-signal py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-red-700 disabled:opacity-60">
                    {savingDiscount ? "Creating…" : "Create code"}
                  </button>
                </div>
              </form>

              <div className="overflow-x-auto border border-obsidian/15 bg-white lg:col-span-2">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-obsidian/15 text-[10px] uppercase tracking-[0.2em] text-obsidian/50">
                    <tr>
                      <th className="p-4">Code</th>
                      <th className="p-4">Discount</th>
                      <th className="p-4">Used</th>
                      <th className="p-4">Expires</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-obsidian/10">
                    {discounts.length === 0 ? (
                      <tr><td colSpan="5" className="p-8 text-center text-obsidian/50">No codes yet. Create one to run a launch deal.</td></tr>
                    ) : discounts.map((d) => (
                      <tr key={d.id} data-testid={`admin-discount-${d.id}`}>
                        <td className="p-4 font-medium uppercase tracking-wide text-obsidian">{d.code}</td>
                        <td className="p-4 text-obsidian/70">{d.type === "percent" ? `${d.value}%` : `$${d.value}`}</td>
                        <td className="p-4 text-obsidian/60">{d.used_count}{d.usage_limit ? ` / ${d.usage_limit}` : ""}</td>
                        <td className="p-4 text-obsidian/60">{d.expires_at ? new Date(d.expires_at).toLocaleDateString() : "—"}</td>
                        <td className="p-4">
                          <div className="flex justify-end">
                            <button data-testid={`delete-discount-${d.id}`} onClick={() => removeDiscount(d.id)}
                              className="border border-obsidian/20 p-2 text-obsidian/70 transition-colors hover:border-signal hover:text-signal">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Product modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-obsidian/50 p-4" onClick={() => setModalOpen(false)}>
          <div data-testid="product-modal" className="max-h-[90vh] w-full max-w-lg overflow-y-auto border border-obsidian/15 bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-obsidian/15 p-5">
              <h2 className="font-serif text-2xl tracking-tighter text-obsidian">{editing ? "Edit product" : "New product"}</h2>
              <button onClick={() => setModalOpen(false)} className="text-obsidian/50 hover:text-signal"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-4 p-6">
              <Field label="Title" testid="form-title">
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="form-title" />
              </Field>
              <Field label="Tagline">
                <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Description">
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={inputCls} />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Price ($)">
                  <input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} data-testid="form-price" />
                </Field>
                <Field label="Format">
                  <input value={form.file_format} onChange={(e) => setForm({ ...form, file_format: e.target.value })} className={inputCls} />
                </Field>
              </div>
              <Field label="Category">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Image URL">
                <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputCls} placeholder="https://…" />
              </Field>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-obsidian/60">Downloadable file</label>
                {editing ? (
                  <div className="mt-2 flex items-center gap-3">
                    <label className="inline-flex cursor-pointer items-center gap-2 border border-obsidian bg-transparent px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-obsidian transition-colors hover:bg-obsidian hover:text-white">
                      <Upload className="h-4 w-4" />
                      {uploading ? "Uploading…" : "Upload file"}
                      <input type="file" data-testid="product-file-input" onChange={uploadFile} className="hidden" disabled={uploading} />
                    </label>
                    {editing.has_file && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-obsidian/60">
                        <Check className="h-4 w-4 text-signal" />
                        {editing.download_filename || "File attached"}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-obsidian/45">Save the product first, then reopen it to attach a downloadable file.</p>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-obsidian/70">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
                Featured product
              </label>
              <button data-testid="save-product-btn" type="submit" disabled={saving} className="w-full bg-signal py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-red-700 disabled:opacity-60">
                {saving ? "Saving…" : editing ? "Update product" : "Create product"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full border border-obsidian/20 bg-bone px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-obsidian focus:ring-offset-1";

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-obsidian/60">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);
